import React, { useCallback, useEffect, useState } from 'react';
import { not, pick, pipe, prop } from 'ramda';
import { useGameFeaturesSelector } from 'js/store/hooks/useGameFeaturesSelector';
import Reset from 'js/components/main/Games/Filters/Reset';
import Tags from './Tags';
import Toggle from 'js/components/main/Games/Filters/Toggle';
import Categories from './Categories';
import Providers from './Providers';
import PayOutVolatility from 'js/components/main/Games/Filters/PayOutVolatility';
import { filterByCategories, filterByProviders, filterByEmptyGames, filterByFeatures, filterByVolatility } from './filters';
import { useAppSelector, useDeviceSelector as useDevice } from 'js/store/hooks';
import { usePortletConfig, usePortletDispatch, usePortletSelector } from 'js/components/system/Portlet';
import { setFilteredCategoriesAction } from 'js/components/main/Games/actions';
import style from './index.scss';

export const FiltersRow = ({
                               isOpen,
                               appliedFiltersCount,
                               toggleFilters,
                               className = ''
                           }) => {
    const {
        showGameCategoriesFilter,
        showPayOutVolatilityScoreFilter,
        isProviders,
        listOfProviders
    } = usePortletConfig();

    return (
        <>
            <div className={`${style.container} ${className}`}>
                <div className={style.content}>
                    <Toggle
                        isOpen={isOpen}
                        appliedFiltersCount={appliedFiltersCount}
                        onClick={toggleFilters}
                    />
                    {isOpen && (
                        <>
                            {showGameCategoriesFilter && <Categories />}
                            {showGameCategoriesFilter && isProviders && <Providers listOfProviders={listOfProviders} />}
                            {showPayOutVolatilityScoreFilter && <PayOutVolatility />}
                        </>
                    )}
                    {appliedFiltersCount > 0 && <Reset />}
                </div>
            </div>
            {isOpen && appliedFiltersCount > 0 && <Tags />}
        </>
    );
};

export const FiltersGrid = ({
                                isOpen,
                                appliedFiltersCount,
                                toggleFilters,
                                className = ''
                            }) => {
    const {
        showPayOutVolatilityScoreFilter,
        isProviders,
        listOfProviders
    } = usePortletConfig();

    return (
        <>
            <div className={`${style.container} ${className}`}>
                <Toggle
                    isOpen={isOpen}
                    appliedFiltersCount={appliedFiltersCount}
                    onClick={toggleFilters}
                />
                {appliedFiltersCount > 0 && <Reset />}
            </div>
            {isOpen && (
                <div className={style.content}>
                    {isProviders && <Providers listOfProviders={listOfProviders} />}
                    {showPayOutVolatilityScoreFilter && <PayOutVolatility />}
                </div>
            )}
        </>
    );
};

export default ({ className }) => {
    const [isOpen, setIsOpen] = useState(false);
    const isDesktop = useDevice(prop('isDesktop'));
    const features = useGameFeaturesSelector(prop('gameFeatures'));
    const {
        categories,
        filters: { selectedCategories, selectedProviders, selectedFeatures, selectedVolatility },
        isSearchActive: isCollapse
    } = usePortletSelector(pick(['categories', 'filters', 'isSearchActive']));
    const dispatch = usePortletDispatch();
    const gamesConfiguration = useAppSelector(prop(['gamesConfiguration']));

    useEffect(() => {
        const nextCategories = pipe(
            filterByCategories(selectedCategories),
            filterByProviders(selectedProviders, gamesConfiguration),
            filterByFeatures(selectedFeatures, features),
            filterByVolatility(selectedVolatility, features),
            filterByEmptyGames
        )(categories);

        dispatch(setFilteredCategoriesAction(nextCategories));
    }, [
        selectedCategories,
        selectedProviders,
        selectedFeatures,
        selectedVolatility,
        gamesConfiguration,
        categories,
        features,
        dispatch
    ]);

    const appliedFiltersCount = selectedCategories.length
        + selectedFeatures.length
        + selectedProviders.length
        + Number(Boolean(selectedVolatility));

    useEffect(() => {
        if (isCollapse && isOpen) {
            setIsOpen(false);
        }
    }, [isCollapse, isOpen]);

    const toggleFilters = useCallback(() => setIsOpen(not), []);
    const filterProps = {
        className,
        isOpen,
        appliedFiltersCount,
        toggleFilters
    };

    return isDesktop
        ? <FiltersRow {...filterProps} />
        : <FiltersGrid {...filterProps} />;
};
